import { useState } from 'react';
import { ApiError, api } from '../api/client';
import { useAuth } from '../auth/AuthProvider';
import { AgreementsPanel } from '../components/AgreementsPanel';
import { InvitationsPanel } from '../components/InvitationsPanel';
import { AsyncError, Banner, EmptyState, Loading } from '../components/states';
import { useAsync } from '../hooks/useAsync';
import { useT } from '../i18n/I18nProvider';

interface ConsoleIdentity {
  id: string;
  name: string;
  fingerprint: string;
  publicKeyPem: string;
  protocolVersion: number;
  createdAt: string;
}

interface OrganizationIdentity {
  id: string;
  organizationId: string;
  organizationName: string;
  federationId: string;
  displayName: string;
  domain: string | null;
  verified: boolean;
  verifiedAt: string | null;
}

interface Trust {
  id: string;
  remoteName: string;
  remoteUrl: string;
  remoteFingerprint: string;
  assurance: string;
  status: string;
  protocolVersion: number;
  encryptionProfile: string;
}

interface Verification {
  id: string;
  method: string;
  status: string;
  evidence: string;
  attestedFingerprint: string | null;
  createdAt: string;
}

/**
 * Federation identity: who this console is, and which organizations on it have had
 * their identity established.
 *
 * The console fingerprint is the page's most important element because it is the
 * value an operator reads to a counterparty over a channel this console does not
 * control — the only step in federation that a compromised console cannot fake on
 * its own. Verification state is shown with a word, not a colour, and always next
 * to the evidence somebody recorded for it: a green tick with no stated basis is
 * the failure this screen exists to prevent.
 */
export function FederationPage(): JSX.Element {
  const t = useT();
  const { can } = useAuth();
  const mayWrite = can('federation:write');

  const identity = useAsync<ConsoleIdentity>(
    () => api.get<ConsoleIdentity>('/federation/console-identity'),
    [],
  );
  const orgs = useAsync<OrganizationIdentity[]>(
    () => api.get<OrganizationIdentity[]>('/federation/organizations'),
    [],
  );

  const trust = useAsync<Trust[]>(() => api.get<Trust[]>('/federation/trust'), []);

  const [trustUrl, setTrustUrl] = useState('');
  const [trustFingerprint, setTrustFingerprint] = useState('');
  const [trustError, setTrustError] = useState<string | null>(null);
  const [trustBusy, setTrustBusy] = useState(false);

  const [selected, setSelected] = useState<string | null>(null);
  const [evidence, setEvidence] = useState('');
  const [fingerprint, setFingerprint] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const history = useAsync<Verification[]>(
    () =>
      selected
        ? api.get<Verification[]>(`/federation/organizations/${selected}/verifications`)
        : Promise.resolve([]),
    [selected],
  );

  const run = async (fn: () => Promise<unknown>): Promise<void> => {
    setError(null);
    setBusy(true);
    try {
      await fn();
      orgs.reload();
      history.reload();
      setEvidence('');
      setFingerprint('');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('errors.unexpected'));
    } finally {
      setBusy(false);
    }
  };

  /**
   * Establishing trust surfaces the reason for a refusal rather than only that it
   * failed: an incompatible peer's blockers are the whole answer to what to do
   * next, and the console already knows them.
   */
  const runTrust = async (fn: () => Promise<unknown>): Promise<void> => {
    setTrustError(null);
    setTrustBusy(true);
    try {
      await fn();
      trust.reload();
      setTrustUrl('');
      setTrustFingerprint('');
    } catch (err) {
      setTrustError(err instanceof ApiError ? describeTrustError(err) : t('errors.unexpected'));
    } finally {
      setTrustBusy(false);
    }
  };

  const establishTrust = (e: React.FormEvent): void => {
    e.preventDefault();
    const fingerprint = trustFingerprint.trim();
    void runTrust(() =>
      api.post('/federation/trust', {
        url: trustUrl.trim(),
        // No fingerprint to compare means nobody verified anything, and the
        // relationship is recorded as the weaker basis it is rather than
        // presented as if it were checked.
        assurance: fingerprint ? 'OUT_OF_BAND_VERIFIED' : 'TRUST_ON_FIRST_USE',
        ...(fingerprint ? { expectedFingerprint: fingerprint } : {}),
      }),
    );
  };

  const trustAction = (id: string, action: 'suspend' | 'resume' | 'revoke'): void => {
    void runTrust(() => api.post(`/federation/trust/${id}/${action}`, {}));
  };

  const attest = (e: React.FormEvent): void => {
    e.preventDefault();
    if (!selected) return;
    void run(() =>
      api.post(`/federation/organizations/${selected}/verifications`, {
        evidence,
        ...(fingerprint.trim() ? { attestedFingerprint: fingerprint.trim() } : {}),
      }),
    );
  };

  const revoke = (organizationId: string): void => {
    void run(() =>
      api.post(`/federation/organizations/${organizationId}/verifications/revoke`, {
        evidence: t('federation.revokedFromConsole'),
      }),
    );
  };

  return (
    <section>
      <div className="page-head">
        <h1>{t('pages.federation.title')}</h1>
      </div>
      <p>{t('federation.intro')}</p>

      <div className="card">
        <h2>{t('federation.consoleIdentity')}</h2>
        <p>{t('federation.fingerprintHint')}</p>
        {identity.loading ? (
          <Loading />
        ) : identity.error ? (
          <AsyncError error={identity.error} onRetry={identity.reload} />
        ) : identity.data ? (
          <dl className="detail-grid">
            <dt>{t('federation.fingerprint')}</dt>
            <dd>
              <code>{identity.data.fingerprint}</code>
            </dd>
            <dt>{t('federation.protocolVersion')}</dt>
            <dd>{identity.data.protocolVersion}</dd>
            <dt>{t('federation.identityCreated')}</dt>
            <dd>{new Date(identity.data.createdAt).toLocaleString()}</dd>
          </dl>
        ) : null}
      </div>

      <div className="card">
        <h2>{t('federation.organizations')}</h2>
        {error ? <Banner variant="error">{error}</Banner> : null}
        {orgs.loading ? (
          <Loading />
        ) : orgs.error ? (
          <AsyncError error={orgs.error} onRetry={orgs.reload} />
        ) : !orgs.data || orgs.data.length === 0 ? (
          <EmptyState hint={t('federation.noOrganizations')} />
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>{t('federation.organization')}</th>
                  <th>{t('federation.federationId')}</th>
                  <th>{t('federation.domain')}</th>
                  <th>{t('federation.status')}</th>
                  <th aria-label="actions" />
                </tr>
              </thead>
              <tbody>
                {orgs.data.map((o) => (
                  <tr key={o.id}>
                    <td>{o.organizationName}</td>
                    <td>
                      <code>{o.federationId}</code>
                    </td>
                    <td>{o.domain ?? '—'}</td>
                    <td>
                      <span className={`chip ${o.verified ? 'chip--online' : 'chip--warning'}`}>
                        {o.verified ? t('federation.verified') : t('federation.unverified')}
                      </span>
                    </td>
                    <td className="row-actions">
                      <button
                        type="button"
                        className="btn btn--ghost"
                        onClick={() => setSelected(o.organizationId)}
                      >
                        {t('federation.viewHistory')}
                      </button>
                      {mayWrite && o.verified ? (
                        <button
                          type="button"
                          className="btn btn--ghost btn--danger"
                          disabled={busy}
                          onClick={() => revoke(o.organizationId)}
                        >
                          {t('federation.revoke')}
                        </button>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <InvitationsPanel organizationId={orgs.data?.[0]?.organizationId} />

      <AgreementsPanel />

      <div className="card">
        <h2>{t('federation.trustedConsoles')}</h2>
        <p>{t('federation.trustIntro')}</p>
        {trustError ? <Banner variant="error">{trustError}</Banner> : null}

        {mayWrite ? (
          <form className="card form" onSubmit={establishTrust}>
            <h3>{t('federation.establishTrust')}</h3>
            <label className="field">
              <span>{t('federation.consoleAddress')}</span>
              <input
                value={trustUrl}
                onChange={(e) => setTrustUrl(e.target.value)}
                required
                placeholder="https://partner.example.com"
              />
            </label>
            <label className="field">
              <span>{t('federation.theirFingerprint')}</span>
              <input
                value={trustFingerprint}
                onChange={(e) => setTrustFingerprint(e.target.value)}
                placeholder="AA:BB:CC:…"
              />
            </label>
            <p className="hint">{t('federation.trustAssuranceHint')}</p>
            <button type="submit" className="btn" disabled={trustBusy}>
              {trustFingerprint.trim()
                ? t('federation.establishVerified')
                : t('federation.establishFirstUse')}
            </button>
          </form>
        ) : null}

        {trust.loading ? (
          <Loading />
        ) : trust.error ? (
          <AsyncError error={trust.error} onRetry={trust.reload} />
        ) : !trust.data || trust.data.length === 0 ? (
          <EmptyState hint={t('federation.noTrust')} />
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>{t('federation.console')}</th>
                  <th>{t('federation.fingerprint')}</th>
                  <th>{t('federation.basis')}</th>
                  <th>{t('federation.status')}</th>
                  <th aria-label="actions" />
                </tr>
              </thead>
              <tbody>
                {trust.data.map((r) => (
                  <tr key={r.id}>
                    <td>
                      {r.remoteName}
                      <br />
                      <small>{r.remoteUrl}</small>
                    </td>
                    <td>
                      <code>{r.remoteFingerprint}</code>
                    </td>
                    <td>
                      <span
                        className={`chip ${r.assurance === 'OUT_OF_BAND_VERIFIED' ? 'chip--online' : 'chip--warning'}`}
                      >
                        {t(`federation.assurance.${r.assurance}`)}
                      </span>
                    </td>
                    <td>{t(`federation.trustStatus.${r.status}`)}</td>
                    <td className="row-actions">
                      {mayWrite && r.status !== 'REVOKED' ? (
                        <>
                          <button
                            type="button"
                            className="btn btn--ghost"
                            disabled={trustBusy}
                            onClick={() =>
                              trustAction(r.id, r.status === 'SUSPENDED' ? 'resume' : 'suspend')
                            }
                          >
                            {r.status === 'SUSPENDED'
                              ? t('federation.resume')
                              : t('federation.suspend')}
                          </button>
                          <button
                            type="button"
                            className="btn btn--ghost btn--danger"
                            disabled={trustBusy}
                            onClick={() => trustAction(r.id, 'revoke')}
                          >
                            {t('federation.revokeTrust')}
                          </button>
                        </>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {selected ? (
        <div className="card">
          <h2>{t('federation.verificationHistory')}</h2>
          <p>{t('federation.appendOnlyHint')}</p>

          {mayWrite ? (
            <form className="card form" onSubmit={attest}>
              <h3>{t('federation.attest')}</h3>
              <p>{t('federation.attestHint')}</p>
              <label className="field">
                <span>{t('federation.evidence')}</span>
                <input
                  value={evidence}
                  onChange={(e) => setEvidence(e.target.value)}
                  required
                  minLength={8}
                  placeholder={t('federation.evidencePlaceholder')}
                />
              </label>
              <label className="field">
                <span>{t('federation.attestedFingerprint')}</span>
                <input
                  value={fingerprint}
                  onChange={(e) => setFingerprint(e.target.value)}
                  placeholder="AA:BB:CC:…"
                />
              </label>
              <button type="submit" className="btn" disabled={busy}>
                {t('federation.recordAttestation')}
              </button>
            </form>
          ) : null}

          {history.loading ? (
            <Loading />
          ) : history.error ? (
            <AsyncError error={history.error} onRetry={history.reload} />
          ) : !history.data || history.data.length === 0 ? (
            <EmptyState hint={t('federation.noVerifications')} />
          ) : (
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>{t('federation.when')}</th>
                    <th>{t('federation.status')}</th>
                    <th>{t('federation.method')}</th>
                    <th>{t('federation.evidence')}</th>
                  </tr>
                </thead>
                <tbody>
                  {history.data.map((v) => (
                    <tr key={v.id}>
                      <td>{new Date(v.createdAt).toLocaleString()}</td>
                      <td>
                        <span
                          className={`chip ${v.status === 'VERIFIED' ? 'chip--online' : 'chip--warning'}`}
                        >
                          {v.status === 'VERIFIED'
                            ? t('federation.verified')
                            : t('federation.revoked')}
                        </span>
                      </td>
                      <td>{t(`federation.methods.${v.method}`)}</td>
                      <td>
                        {v.evidence}
                        {v.attestedFingerprint ? (
                          <>
                            {' '}
                            <code>{v.attestedFingerprint}</code>
                          </>
                        ) : null}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      ) : null}
    </section>
  );
}

/**
 * Turn a refused handshake into something an operator can act on.
 *
 * The controller returns the negotiation alongside the message when a peer is
 * incompatible. Showing only "not compatible" would send somebody to ask the
 * other side what is missing, when this console was already told.
 */
function describeTrustError(err: ApiError): string {
  const negotiation = (err.details as { negotiation?: { findings?: Finding[] } } | undefined)
    ?.negotiation;
  const blockers = (negotiation?.findings ?? []).filter((f) => f.severity === 'blocker');
  if (blockers.length === 0) return err.message;
  return `${err.message} ${blockers.map((f) => f.detail).join(' ')}`;
}

interface Finding {
  code: string;
  severity: string;
  detail: string;
}
